import React from 'react';
import { GlobalCameraContext } from '@src/core/providers/global-camera-provider';
import { CameraViewMode, CameraSource } from './types';

/**
 * Thin backward-compatible shim over GlobalCameraProvider.
 *
 * Pages keep using `useCameraLayout()` and `setCameraSource(...)`, but the
 * actual WebRTC lifecycle lives in the global provider so the camera
 * survives navigation between feature roots.
 */
interface CameraLayoutContextValue {
  viewMode: CameraViewMode;
  setViewMode: (mode: CameraViewMode) => void;
  cameraSource: CameraSource | null;
  setCameraSource: (source: CameraSource | null) => void;
  reconnect: () => void;
  isReconnecting: boolean;
  registerReconnect: (fn: () => void, isReconnecting: boolean) => void;
  collapsed: boolean;
  toggleCollapse: () => void;
}

const noop = () => {};

const FALLBACK_VALUE: CameraLayoutContextValue = {
  viewMode: 'default',
  setViewMode: noop,
  cameraSource: null,
  setCameraSource: noop,
  reconnect: noop,
  isReconnecting: false,
  registerReconnect: noop,
  collapsed: false,
  toggleCollapse: noop,
};

export const useCameraLayout = (): CameraLayoutContextValue => {
  const global = React.useContext(GlobalCameraContext);

  const setCameraSource = React.useCallback(
    (source: CameraSource | null) => {
      if (!global) return;
      if (source) {
        global.connectCamera(source);
      } else {
        global.disconnectCamera(false);
      }
    },
    [global],
  );

  if (!global) return FALLBACK_VALUE;

  return {
    viewMode: global.viewMode,
    setViewMode: global.setViewMode,
    cameraSource: global.activeSource,
    setCameraSource,
    reconnect: global.reconnect,
    isReconnecting: global.isReconnecting,
    registerReconnect: noop,
    collapsed: global.collapsed,
    toggleCollapse: global.toggleCollapse,
  };
};

/**
 * Kept for backward compatibility so existing feature roots can still wrap
 * their subtree with <CameraLayoutProvider>. It's a pass-through now — the
 * real provider (GlobalCameraProvider) lives above the router.
 */
export const CameraLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);
